"use client"

import { useState } from "react"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { Brain, BookOpen } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { getMemoryHref, isMemorySpecificLink } from "@/lib/types/citations"
import { trackCitationClick } from "@/lib/analytics/citation-analytics"
import { FF_CITATIONS_PLACEHOLDER_MODE } from "@/lib/config/feature-flags"

interface MemoryCitationPillProps {
  /** Display label (will be truncated with ellipsis if too long) */
  label: string
  /** Full label for tooltip (optional, defaults to label) */
  fullLabel?: string
  /** Domain slug for routing (e.g., "marketing", "strategy") */
  domain?: string
  /** Node slug for specific memory node */
  nodeSlug?: string
  /** Memory ID for specific memory record */
  memoryId?: string
  /** Link destination - uses getMemoryHref if not provided */
  href?: string
  /** Conversation ID for telemetry */
  conversationId?: string
  /** Force placeholder mode (for testing) */
  forcePlaceholder?: boolean
  className?: string
}

const MAX_LABEL_LENGTH = 24

/**
 * MemoryCitationPill - Outlined pill link for internal memory citations
 *
 * - 1px ring outline (increases to ring-2 on hover)
 * - Left icon: Brain (memory-specific) or Book (section/overview)
 * - Text color uses accent token
 * - Truncation with ellipsis + tooltip for full label
 * - Placeholder mode: non-clickable, shows "Placeholder (design mode)" tooltip
 */
export function MemoryCitationPill({
  label,
  fullLabel,
  domain,
  nodeSlug,
  memoryId,
  href,
  conversationId,
  forcePlaceholder,
  className,
}: MemoryCitationPillProps) {
  const [isHovered, setIsHovered] = useState(false)

  const isPlaceholder = forcePlaceholder || FF_CITATIONS_PLACEHOLDER_MODE

  // Compute href from memoryId/domain if not provided
  const computedHref = href || getMemoryHref({ memory_id: memoryId, domainSlug: domain })

  // Determine icon based on whether we have a specific memory ID
  const isMemoryLink = memoryId ? true : isMemorySpecificLink(computedHref)
  const Icon = isMemoryLink ? Brain : BookOpen

  // Truncate label if needed
  const displayLabel = label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH)}…` : label
  const needsTooltip = label.length > MAX_LABEL_LENGTH || fullLabel || isPlaceholder

  const tooltipText = isPlaceholder ? "Placeholder (design mode)" : fullLabel || label

  const handleClick = () => {
    if (isPlaceholder) return // No tracking in placeholder mode

    // Fire-and-forget telemetry
    trackCitationClick({
      type: "memory",
      id: memoryId || domain || "unknown",
      source_domain: domain,
      position: "pill",
      conversation_id: conversationId,
    })
  }

  const pillClassName = cn(
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-full",
    "text-xs font-medium whitespace-nowrap",
    // Ring-based styling (no border to avoid reflow)
    "ring-1 ring-inset transition-all duration-150",
    isHovered ? "ring-2 ring-[var(--brain)] bg-[var(--brain)]/10" : "ring-[var(--brain)]/40 bg-transparent",
    // Text color
    "text-[var(--brain)]",
    // Placeholder mode: disabled cursor
    isPlaceholder && "cursor-default",
    className,
  )

  const pillContent = isPlaceholder ? (
    <span onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={pillClassName}>
      <Icon className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
      <span>{displayLabel}</span>
    </span>
  ) : (
    <Link
      href={computedHref}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={pillClassName}
    >
      <Icon className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
      <span>{displayLabel}</span>
    </Link>
  )

  if (needsTooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{pillContent}</TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-slate-200 text-xs max-w-[280px]">
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return pillContent
}
